import React from 'react';
import { Package, Lightbulb, MessageCircle, ClipboardList } from 'lucide-react';

/**
 * Presentation metadata for the 4 use cases, ported from the breakfast mockups
 * (Free_account_study.html / pay_per_study.html). Each card has a 44px pastel
 * icon tile, a title, a description, and a colored "included" sub-line. The
 * categorical tile colors are kept as in the mockup, except chat's blue tile is
 * swapped to our teal brand. Keyed by the backend use-case key.
 */
export interface UseCaseMeta {
  title: string;
  icon: React.ReactNode;
  tileBg: string; // hex for the icon tile background
  iconColor: string; // hex for the icon stroke
  desc: string;
  limit: string;
  limitColor: string; // hex matching the icon, for the "included" line
}

const ICON = 'h-[22px] w-[22px]';

export const USE_CASE_META: Record<string, UseCaseMeta> = {
  packaging: {
    title: 'Packaging test',
    icon: <Package className={ICON} style={{ color: '#534AB7' }} />,
    tileBg: '#EEEDFE',
    iconColor: '#534AB7',
    desc: 'Evaluate a packaging design for shelf appeal, clarity, and brand fit with real consumer archetypes.',
    limit: '1 design included',
    limitColor: '#534AB7',
  },
  concept: {
    title: 'Concept test',
    icon: <Lightbulb className={ICON} style={{ color: '#854F0B' }} />,
    tileBg: '#FAEEDA',
    iconColor: '#854F0B',
    desc: 'Test a product or campaign concept for appeal, purchase intent, and fit with your target consumer.',
    limit: '1 study included',
    limitColor: '#854F0B',
  },
  chat: {
    title: 'Chat',
    // Mockup uses blue here; swapped to our teal brand.
    icon: <MessageCircle className={ICON} style={{ color: '#07E5D1' }} />,
    tileBg: '#E8FCFA',
    iconColor: '#07E5D1',
    desc: 'Ask open-ended questions in natural conversation. Get answers as your consumer would give them.',
    limit: '3 queries included',
    limitColor: '#0B7C74',
  },
  survey: {
    title: 'Qual / quant survey',
    icon: <ClipboardList className={ICON} style={{ color: '#0F6E56' }} />,
    tileBg: '#E1F5EE',
    iconColor: '#0F6E56',
    desc: 'Field a structured survey across your audience segment. Quantitative results delivered in minutes.',
    limit: 'Full survey run included',
    limitColor: '#0F6E56',
  },
};

export const USE_CASE_ORDER = ['packaging', 'concept', 'chat', 'survey'] as const;

export const useCaseIcon = (key: string) => USE_CASE_META[key]?.icon ?? null;
export const useCaseIncluded = (key: string) => USE_CASE_META[key]?.limit ?? '';

/** The 2×2 use-case card from the mockups (icon tile + title + desc + limit). */
export const UseCaseCard: React.FC<{
  useCaseKey: string;
  selected: boolean;
  onClick: () => void;
}> = ({ useCaseKey, selected, onClick }) => {
  const m = USE_CASE_META[useCaseKey];
  if (!m) return null;
  return (
    <div
      onClick={onClick}
      className={[
        'flex cursor-pointer flex-col gap-3.5 rounded-xl border-[1.5px] p-5 transition-colors',
        selected ? 'border-primary bg-[#E8FCFA]' : 'border-black/10 bg-white hover:border-black/20',
      ].join(' ')}
    >
      <div
        className="flex h-11 w-11 items-center justify-center rounded-lg"
        style={{ background: m.tileBg }}
      >
        {m.icon}
      </div>
      <div>
        <div className="text-[15px] font-medium text-[#111]">{m.title}</div>
        <div className="mt-1 text-[13px] leading-normal text-[#6b6b6b]">{m.desc}</div>
      </div>
      <div className="mt-auto pt-1 text-[11px]" style={{ color: m.limitColor }}>
        {m.limit}
      </div>
    </div>
  );
};

/** Loading placeholder matching the UseCaseCard shape (avoids a sudden pop-in). */
export const UseCaseCardSkeleton: React.FC = () => (
  <div className="flex flex-col gap-3.5 rounded-xl border border-black/10 bg-white p-5">
    <div className="h-11 w-11 animate-pulse rounded-lg bg-black/[0.06]" />
    <div className="space-y-2">
      <div className="h-3.5 w-1/2 animate-pulse rounded bg-black/[0.06]" />
      <div className="h-3 w-full animate-pulse rounded bg-black/[0.06]" />
      <div className="h-3 w-4/5 animate-pulse rounded bg-black/[0.06]" />
    </div>
    <div className="mt-auto h-2.5 w-1/3 animate-pulse rounded bg-black/[0.06]" />
  </div>
);

/** Small inline icon tile (used in summary bars). */
export const UseCaseTile: React.FC<{ useCaseKey: string; size?: number }> = ({
  useCaseKey,
  size = 32,
}) => {
  const m = USE_CASE_META[useCaseKey];
  if (!m) return null;
  return (
    <div
      className="flex items-center justify-center rounded-lg flex-shrink-0"
      style={{ background: m.tileBg, width: size, height: size }}
    >
      {React.isValidElement(m.icon)
        ? React.cloneElement(m.icon as React.ReactElement, {
            className: 'h-[17px] w-[17px]',
          })
        : m.icon}
    </div>
  );
};
